import * as Inventory from '../inventory';
import {
  DecorationItemID,
  decorationItemsMap,
  DecorationTierID,
  tiersItemsMap,
} from '../decoration/decoration.ruleset';

import {
  AccessoryItemID,
  accessoryItemsMap,
  AccessoryTierID,
  tiersAccessoryItemsMap,
} from '../accessory/accessory.ruleset';
import { State } from '../State';
import {
  AccessoryCatalogItem,
  CatalogItem,
  CatalogItemId,
  CatalogItemType,
  CatalogTierId,
  DecorationCatalogItem,
} from './catalog.ruleset';
import { assertNever } from '../utils';

/**
 * Get catalog.
 * @param state
 */
export function getCatalog(state: State): CatalogItem[] {
  const decorationItems = getDecorationCatalog(state);
  const accessoryItems = getAccessoryCatalog(state);

  return [...decorationItems, ...accessoryItems];
}

/**
 * Get catalog item.
 */
export function getCatalogItem(
  state: State,
  type: CatalogItemType,
  itemId: CatalogItemId,
  tierId: CatalogTierId,
): CatalogItem {
  switch (type) {
    case 'decoration':
      return getDecorationCatalogItem(
        state,
        itemId as DecorationItemID,
        tierId as DecorationTierID,
      );
    case 'accessory':
      return getAccessoryCatalogItem(
        state,
        itemId as AccessoryItemID,
        tierId as AccessoryTierID,
      );
    default:
      assertNever(type);
  }
}

/**
 * Get decoration catalog.
 * @param state
 * @returns
 */
function getDecorationCatalog(state: State): CatalogItem[] {
  return Object.keys(state.decoration.purchased).reduce((acc, itemId) => {
    const item = state.decoration.purchased[itemId];

    Object.keys(item.tier).forEach((tierId) => {
      acc.push(
        getDecorationCatalogItem(
          state,
          itemId as DecorationItemID,
          tierId as DecorationTierID,
        ),
      );
    });

    return acc;
  }, [] as CatalogItem[]);
}

/**
 * Get catalog item.
 * @param state
 * @param itemId
 * @param tierId
 */
function getDecorationCatalogItem(
  state: State,
  itemId: DecorationItemID,
  tierId: DecorationTierID,
): CatalogItem {
  const item = state.decoration.purchased[itemId];
  const tier = item.tier[tierId];

  const quantity = tier?.quantity ?? 0;

  const result = {
    ...getDecorationItemConfig(itemId, tierId)!,
    type: 'decoration',
    quantity,
    isNew: tier?.isNew ?? false,
  } as const;

  return { ...result, canFuse: canFuse(state, result, true) };
}

/**
 * Get decoration catalog.
 * @param state
 * @returns
 */
function getAccessoryCatalog(state: State): CatalogItem[] {
  return Object.keys(state.accessory.purchased).reduce((acc, itemId) => {
    const item = state.accessory.purchased[itemId];

    Object.keys(item.tier).forEach((tierId) => {
      acc.push(
        getAccessoryCatalogItem(
          state,
          itemId as AccessoryItemID,
          tierId as AccessoryTierID,
        ),
      );
    });

    return acc;
  }, [] as CatalogItem[]);
}
/**
 * Get catalog item.
 * @param state
 * @param itemId
 * @param tierId
 */
function getAccessoryCatalogItem(
  state: State,
  itemId: AccessoryItemID,
  tierId: AccessoryTierID,
): CatalogItem {
  const item = state.accessory.purchased[itemId];
  const tier = item.tier[tierId];

  const quantity = tier?.quantity ?? 0;

  const result = {
    ...getAccessoryItemConfig(itemId, tierId)!,
    type: 'accessory',
    quantity,
    isNew: tier?.isNew ?? false,
  } as const;

  return { ...result, canFuse: canFuse(state, result, true) };
}

/**
 * Check if item can be fused.
 *
 * @param state
 * @param itemId
 * @param tierId
 */
export function canFuse(
  state: State,
  item: CatalogItem,
  ignoreCurrencyRequirements = false,
): boolean {
  // Destructure item.
  const { canFuse, id, tierId, type, fuseItemsRequired, fusePrice } = item;

  // This item can't be fused.
  if (!canFuse) return false;

  // Check if user has enough items and currency.
  const { purchased } = state[type];
  const quantity = purchased?.[id].tier[tierId]?.quantity ?? 0;

  return (
    quantity >= fuseItemsRequired &&
    (Inventory.getters.getPoints(state) >= fusePrice ||
      ignoreCurrencyRequirements)
  );
}

/**
 * Check if item can be sold.
 */
export function canSell(state: State, item: CatalogItem): boolean {
  // Destructure item.
  const { canSell } = item;

  return canSell;
}
/**
 * Get config data for a decoration item.
 * @param itemId
 * @param tierId
 */
export function getDecorationItemConfig(
  itemId: DecorationItemID,
  tierId: DecorationTierID,
): Omit<DecorationCatalogItem, 'type' | 'quantity' | 'isNew'> | undefined {
  const itemConfig = decorationItemsMap.get(itemId);
  if (!itemConfig) return undefined;

  const tierConfig = tiersItemsMap[itemId][tierId];

  if (!tierConfig) return undefined;

  return {
    id: itemId,
    theme: itemConfig.theme,
    room: itemConfig.room,
    image: tierConfig.image,
    tier: tierConfig.tier,
    name_jp: itemConfig.name_jp,
    name_en: itemConfig.name_en,
    canFuse: tierConfig.can_fuse,
    canSell: tierConfig.can_sell,
    fuseCurrency: tierConfig.fuse_currency!,
    fusePrice: tierConfig.fuse_price!,
    sellCurrency: tierConfig.sell_currency!,
    sellPrice: tierConfig.sell_price!,
    fuseItemsRequired: tierConfig.fuse_items_required!,
    nextTierId: tierConfig.fuse_next_tier as DecorationTierID,
    maxTier: itemConfig.max_tier,
    tierId,
  };
}

/**
 * Get config data for an accessory item.
 * @param itemId
 * @param tierId
 */
export function getAccessoryItemConfig(
  itemId: AccessoryItemID,
  tierId: AccessoryTierID,
): Omit<AccessoryCatalogItem, 'type' | 'quantity' | 'isNew'> | undefined {
  const itemConfig = accessoryItemsMap.get(itemId)!;
  const tierConfig = tiersAccessoryItemsMap[itemId][tierId];

  if (!tierConfig) return undefined;

  return {
    id: itemId,
    theme: itemConfig.theme,
    image: tierConfig.image,
    tier: tierConfig.tier,
    name_jp: itemConfig.name_jp,
    name_en: itemConfig.name_en,
    canSell: tierConfig.can_sell,
    canFuse: tierConfig.can_fuse,
    fuseCurrency: tierConfig.fuse_currency!,
    fusePrice: tierConfig.fuse_price!,
    sellCurrency: tierConfig.sell_currency!,
    sellPrice: tierConfig.sell_price!,
    fuseItemsRequired: tierConfig.fuse_items_required!,
    nextTierId: tierConfig.fuse_next_tier as AccessoryTierID,
    maxTier: itemConfig.max_tier,
    slot: itemConfig.slot,
    tierId,
  };
}

/**
 * Get amount of duplicate items.
 * @param state
 * @param itemId
 * @param tierId
 */
export function getDuplicateItems(
  state: State,
  itemId: string,
  tierId: string,
): number {
  const item = state.decoration.purchased[itemId];
  const tier = item?.tier[tierId];
  return tier?.quantity ?? 0;
}
